// The Files tab: commits, the files of the selected commit, and the selected file, side by side.
// Less wide, the commits sit above the files; narrow, it shows one column at a time and moves
// along as you pick. A folder without git shows its files and the file. New commits show up
// while the tab is open.
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { checkForCommits, openRepo, useFiles } from '../state/files';
import { useWorkspace } from '../state/workspace';
import { CommitList } from './CommitList';
import { FileColumn } from './FileColumn';
import { FilePane } from './FilePane';

const POLL_MS = 4000;
/** Narrower than this, one column at a time. */
const NARROW = 640;
/** At least this wide, commits and files side by side; between the two, one above the other. */
const WIDE = 1180;
/** Widths of the commit and file columns. */
const COMMITS_WIDTH = 300;
const FILES_WIDTH = 320;
/** A file column narrower than this shows a diff in one column. */
const SPLIT_DIFF = 620;

type Pane = 'commits' | 'files' | 'file';

function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <button type="button" onClick={onBack} className="flex h-9 w-full flex-none items-center gap-1.5 border-b border-border px-2 text-[12.5px] text-muted hover:text-text pointer-coarse:h-11">
      <ArrowLeft size={14} /> {label}
    </button>
  );
}

export function FilesView() {
  const source = useWorkspace((s) => s.source);
  const repo = useFiles((s) => s.repo);
  const host = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1200);
  const [pane, setPane] = useState<Pane>('commits');
  const git = repo.state === 'ready';

  useEffect(() => {
    if (source && repo.state === 'idle') void openRepo();
  }, [source, repo.state]);

  // New commits, a branch switch or a fetch: looked for while the tab is open and the window visible.
  useEffect(() => {
    if (!git) return;
    const check = () => document.visibilityState === 'visible' && void checkForCommits();
    const timer = window.setInterval(check, POLL_MS);
    window.addEventListener('focus', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [git]);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={host} className="h-full min-h-0 bg-bg">
      <Columns width={width} pane={pane} setPane={setPane} />
    </div>
  );
}

function Columns({ width, pane, setPane }: { width: number; pane: Pane; setPane: (pane: Pane) => void }) {
  const repo = useFiles((s) => s.repo);
  const git = repo.state === 'ready';

  if (repo.state === 'idle' || repo.state === 'loading') {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" /> Reading the history…
      </div>
    );
  }

  if (width < NARROW) {
    // Without git there is no commit column: files, then the file.
    const current: Pane = !git && pane === 'commits' ? 'files' : pane;
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="files-view" data-pane={current}>
        {current === 'commits' && <CommitList onPick={() => setPane('files')} />}
        {current === 'files' && (
          <>
            {git && <BackBar label="Commits" onBack={() => setPane('commits')} />}
            <div className="min-h-0 flex-1">
              <FileColumn onPick={() => setPane('file')} />
            </div>
          </>
        )}
        {current === 'file' && <FilePane narrow onBack={() => setPane('files')} />}
      </div>
    );
  }

  if (git && width < WIDE) {
    return (
      <div className="flex h-full min-h-0" data-testid="files-view" data-pane="stacked">
        <div className="flex flex-none flex-col border-r border-border bg-panel" style={{ width: FILES_WIDTH }}>
          <div className="min-h-0 flex-[1_1_42%] border-b border-border">
            <CommitList />
          </div>
          <div className="min-h-0 flex-[1_1_58%]">
            <FileColumn />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <FilePane narrow={width - FILES_WIDTH < SPLIT_DIFF} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0" data-testid="files-view" data-pane="all">
      {git && (
        <div className="flex-none border-r border-border bg-panel" style={{ width: COMMITS_WIDTH }}>
          <CommitList />
        </div>
      )}
      <div className="flex-none border-r border-border bg-panel" style={{ width: FILES_WIDTH }}>
        <FileColumn />
      </div>
      <div className="min-w-0 flex-1">
        <FilePane narrow={width - FILES_WIDTH - (git ? COMMITS_WIDTH : 0) < SPLIT_DIFF} />
      </div>
    </div>
  );
}
